import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { HOST_API_URL } from '../../../../config-global';
import { useTabs } from '../../../../context/TabContext';
import { useSelector } from '../../../../redux/store';
import FileThumbnail from '../../../../reusable-components/file-thumbnail';
import Iconify from '../../../../reusable-components/iconify';
import Lightbox from '../../../../reusable-components/lightbox';
import { fMonthDayTimeYear } from '../../../../utils/formatTime';
import { getTicketSharedContent } from './newTicket/TicketHelper';

TicketSharedContent.propTypes = {
  fullWidth: PropTypes.bool, // mobile: panel covers the whole screen
};

export default function TicketSharedContent({ fullWidth }) {
  const location = useLocation();
  const { id = '' } = useParams();
  const subticketId = new URLSearchParams(location.search).get('subticket');
  const { ticketMessages } = useSelector((state) => state.tickets.data.ticketById[subticketId || id] || {});
  const { addNewTabAndNavigate } = useTabs();

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('media');
  const [selectedImage, setSelectedImage] = useState(-1);

  const { media, docs, links } = useMemo(() => getTicketSharedContent(ticketMessages), [ticketMessages]);
  const total = media.length + docs.length + links.length;

  const sameSite = (url) => url.includes('localhost:44475/') || url.includes('app.platformsolutions.com');

  const openDoc = (doc) =>
    window.open(`${HOST_API_URL}Api/TicketFile?noteId=${doc.messageId}${doc.isPdf ? '&download=false' : ''}`, '_blank');

  const openLink = (url) => {
    if (sameSite(url)) {
      setOpen(false);
      addNewTabAndNavigate(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const getDomain = (url) => {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  };

  const emptyMessage = (text) => (
    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
      {text}
    </Typography>
  );

  return (
    <>
      <ListItemButton onClick={() => setOpen(true)} sx={{ px: 0, borderRadius: 1 }}>
        <Iconify icon="mdi:paperclip" width={20} height={20} sx={{ mr: 1, color: 'info.main' }} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Media, links and docs
        </Typography>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          {total}
        </Typography>
        <Iconify icon="mdi:chevron-right" width={20} height={20} sx={{ color: 'text.secondary' }} />
      </ListItemButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: fullWidth ? '100%' : 400, maxWidth: '100%' } }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 1, pt: 1 }}>
          <IconButton onClick={() => setOpen(false)}>
            <Iconify icon={fullWidth ? 'mingcute:left-fill' : 'mdi:close'} width={22} height={22} />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1 }}>
            Media, links and docs
          </Typography>
        </Stack>

        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth" sx={{ px: 2 }}>
          <Tab value="media" label={`Media (${media.length})`} />
          <Tab value="docs" label={`Docs (${docs.length})`} />
          <Tab value="links" label={`Links (${links.length})`} />
        </Tabs>

        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          {tab === 'media' &&
            (media.length ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
                {media.map((item, index) => (
                  <Box
                    key={`${item.messageId}-${index}`}
                    component="img"
                    src={item.src}
                    alt=""
                    title={`${item.name} · ${fMonthDayTimeYear(item.sentOn)}`}
                    onClick={() => setSelectedImage(index)}
                    sx={{
                      width: '100%',
                      aspectRatio: '1',
                      objectFit: 'cover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: 'grey.200',
                      '&:hover': { filter: 'brightness(0.9)' },
                    }}
                  />
                ))}
              </Box>
            ) : (
              emptyMessage('No media shared in this ticket')
            ))}

          {tab === 'docs' &&
            (docs.length ? (
              <List disablePadding>
                {docs.map((doc) => (
                  <ListItemButton key={doc.messageId} onClick={() => openDoc(doc)} divider sx={{ px: 1 }}>
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <FileThumbnail file={doc.fileName} sx={{ width: 32, height: 32 }} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={doc.fileName}
                      secondary={`${doc.name} · ${fMonthDayTimeYear(doc.sentOn)}`}
                      primaryTypographyProps={{ variant: 'subtitle2', noWrap: true }}
                      secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                    />
                    <Iconify
                      icon={doc.isPdf ? 'mdi:eye-outline' : 'mdi:download'}
                      width={20}
                      height={20}
                      sx={{ ml: 1, color: 'text.secondary', flexShrink: 0 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              emptyMessage('No docs shared in this ticket')
            ))}

          {tab === 'links' &&
            (links.length ? (
              <List disablePadding>
                {links.map((link) => (
                  <ListItemButton key={link.url} onClick={() => openLink(link.url)} divider sx={{ px: 1 }}>
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar variant="rounded" sx={{ bgcolor: 'info.lighter', color: 'info.main', width: 36, height: 36 }}>
                        <Iconify icon="mdi:link-variant" width={20} height={20} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={link.text || link.url}
                      secondary={
                        <>
                          <Typography component="span" variant="caption" sx={{ display: 'block', color: 'info.main' }} noWrap>
                            {getDomain(link.url)}
                          </Typography>
                          <Typography component="span" variant="caption" sx={{ display: 'block' }} noWrap>
                            {`${link.name} · ${fMonthDayTimeYear(link.sentOn)}`}
                          </Typography>
                        </>
                      }
                      primaryTypographyProps={{ variant: 'subtitle2', noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              emptyMessage('No links shared in this ticket')
            ))}
        </Box>
      </Drawer>

      <Lightbox
        index={selectedImage}
        slides={media.map((item) => ({ src: item.src }))}
        open={selectedImage >= 0}
        close={() => setSelectedImage(-1)}
      />
    </>
  );
}
